<template>
  <div class="cart-page-aside">
    <div class="cart-page-aside__main">
      <div class="cart-page-aside__total">
        <span>Итого</span>
        <span>{{ orderSum | formatPrice }}</span>
      </div>
      <ul class="cart-page-aside__info">
        <li v-if="isAuthorized" class="cart-page__bonuses">
          <div class="cart-page__bonuses-balance">
            <div class="cart-page__bonuses-balance-left">
              <span class="cart-page__bonuses-balance-title"> Баланс баллов </span>
              <span class="cart-page__bonuses-balance-subtitle"> 1 балл = 1 ₽ </span>
            </div>
            <span class="cart-page__bonuses-balance-value">
              {{ userBonusBalance | formatNumber }} <IconComponent name="buy-crypto" />
            </span>
          </div>
          <SwitchComponent v-model="useBonusesModel" reverse full-width> Списать баллы </SwitchComponent>
        </li>
        <li>
          <span>Товаров на сумму</span>
          <span>{{ cartItemsSum | formatPrice }}</span>
        </li>
        <li v-if="discount" class="cart-page-aside__discount">
          <span>Экономия</span>
          <span>{{ discount | formatPrice }}</span>
        </li>
        <li>
          <span>Добавится баллов</span>
          <span class="cart-page__bonuses-balance-value">{{ bonusesEarned | formatNumber }}</span>
        </li>
        <li v-if="useBonuses && isAuthorized">
          <span>Баллы</span>
          <span>-{{ maxWriteOff | formatNumber }}</span>
        </li>
      </ul>
      <div class="cart-page-aside__pay">
        <button type="button" class="btn btn--md btn--main" :disabled="paymentDisabled" @click="pay">
          <FileIconComponent v-if="loading" name="loading" />
          <template v-else>Оформить заказ</template>
        </button>
        <TermsAgreeComponent />
      </div>
      <div class="cart-page-aside__promocode">
        <InputComponent
          v-model.trim="promocode.value"
          :error="promocode.error"
          title="У меня есть промокод"
          icon-position="right"
          @action="checkPromocode"
          @enter="checkPromocode"
        >
          <template v-slot:icon>
            <FileIconComponent v-if="loadingPromocode" name="loading" />
            <IconComponent v-else name="arrow-right-1" />
          </template>
        </InputComponent>
        <div v-if="activePromocode" class="cart-page-aside__promocode-active">
          <span>{{ activePromocode }}</span>
          <button @click="activePromocode = null" type="button" class="btn">
            <IconComponent name="close" />
          </button>
        </div>
      </div>
    </div>
    <div class="cart-page-aside__share">
      <button
        type="button"
        class="btn btn--md"
        :class="sharedCartCopied ? 'btn--main' : 'btn--main-outline'"
        :disabled="orderSum === 0 || shareLoading"
        @click="shareCart"
      >
        <FileIconComponent v-if="shareLoading" name="loading" />
        <template v-else>
          <IconComponent :name="sharedCartCopied ? 'icon-tick-square' : 'icon-export-circle'" />
          <span>
            {{ sharedCartCopied ? "Ссылка скопирована" : "Поделиться корзиной" }}
          </span>
        </template>
      </button>
      <button
        type="button"
        class="btn btn--md btn--main-outline"
        :disabled="orderSum === 0 || pdfLoading"
        @click="downloadPdf"
      >
        <FileIconComponent v-if="pdfLoading" name="loading" />
        <template v-else>
          <IconComponent name="document-upload" />
          <span>Скачать файлом</span>
        </template>
      </button>
    </div>
  </div>
</template>

<script>
import FileIconComponent from "components/FileIconComponent.vue";
import TermsAgreeComponent from "components/TermsAgreeComponent.vue";
import InputComponent from "components/inputs/InputComponent.vue";
import IconComponent from "components/IconComponent.vue";
import SHARE_PROMOCODE from "@/graphql/queries/share_promocode.graphql";
import CREATE_SHARED_CART from "@/graphql/mutations/create_shared_cart.graphql";
import SwitchComponent from "components/inputs/SwitchComponent.vue";

export default {
  name: "CartPageAsideComponent",
  props: {
    delivery: Object,
    loading: Boolean,
    useBonuses: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      agree: true,
      loadingPromocode: false,
      showPromoField: false,
      activePromocode: undefined,
      pdfLoading: false,
      shareLoading: false,
      promocode: {
        value: undefined,
        error: undefined,
      },
      sharedCartCopied: false,
    };
  },
  computed: {
    cartItems() {
      return this.$store.state.auth.cart.filter((i) => i.item);
    },
    paymentDisabled() {
      return !(this.agree && this.orderSum);
    },
    isAuthorized() {
      return !!this.$store.state.auth?.user;
    },
    userBonusBalance() {
      return this.$store.state.auth?.user?.bonus_balance || 0;
    },
    cartItemsSum() {
      return this.cartItems
        .map((r) => this.$options.filters.share_price(r.item?.price, r.item) * r.count)
        .reduce((a, b) => a + b, 0);
    },
    orderSum() {
      // Вычитаем бонусы, если пользователь выбрал их списание
      return this.useBonuses && this.isAuthorized ? this.cartItemsSum - this.maxWriteOff : this.cartItemsSum;
    },
    useBonusesModel: {
      get() {
        return this.useBonuses;
      },
      set(value) {
        this.$emit("update:use-bonuses", value);
      },
    },
    discount() {
      return (
        this.cartItems.map((r) => parseFloat(r.item?.price) * r.count).reduce((a, b) => a + b, 0) -
        this.cartItemsSum
      );
    },
    maxWriteOff() {
      let max = Math.round(this.cartItemsSum * 0.3);

      // Если максимально возможное списание больше, чем есть баллов, то спишется количество баллов
      return max > this.userBonusBalance ? this.userBonusBalance : max;
    },
    bonusesEarned() {
      return Math.round(this.orderSum * 0.03);
    },
  },
  methods: {
    /**
     * Скачивание корзины в PDF
     */
    async downloadPdf() {
      if (this.pdfLoading) return;

      try {
        // Показываем индикатор загрузки
        this.pdfLoading = true;

        // Проверяем, что корзина не пуста
        if (this.orderSum === 0) {
          this.$notify({
            type: "error",
            title: "Ошибка",
            text: "Корзина пуста",
          });
          return;
        }

        // Открываем PDF страницу в новом окне
        const pdfWindow = window.open(
          this.$router.resolve({ name: "download-pdf", query: { type: "cart" } }).href,
          "_blank"
        );

        if (!pdfWindow) {
          // Если блокировщик попапов заблокировал окно, переходим на страницу в текущем окне
          this.$router.push({ name: "download-pdf", query: { type: "cart" } });
        }
      } catch (error) {
        // Обработка ошибки
        console.error("Ошибка при открытии PDF страницы:", error);
        this.$notify({
          type: "error",
          title: "Ошибка",
          text: "Произошла ошибка при открытии PDF страницы",
        });
      } finally {
        // Скрываем индикатор загрузки
        this.pdfLoading = false;
      }
    },
    pay() {
      if (this.agree) {
        this.$emit("pay");
      }
    },
    async checkPromocode() {
      if (!this.loadingPromocode && this.promocode) {
        this.promocode.error = undefined;
        this.loadingPromocode = true;
        await this.$apollo.provider.defaultClient
          .query({
            query: SHARE_PROMOCODE,
            variables: {
              promocode: this.promocode.value,
            },
          })
          .then(({ data }) => {
            this.loadingPromocode = false;
            if (data.share_promocode) {
              this.activePromocode = this.promocode.value;
              this.$emit("setPromocode", data.share_promocode, this.promocode.value);
              this.$notify({
                title: "Успешно",
                text: "Промокод активирован",
                type: "success",
              });
            } else {
              this.promocode.error = "Неверный промокод";
            }
          })
          .catch(() => {
            this.loadingPromocode = false;
            this.promocode.error = "Неверный промокод";
          });
      }
    },
    removePromocode() {
      this.activePromocode = undefined;
      this.$emit("removePromocode");
    },

    /**
     * Создание общей корзины для поделиться
     */
    async shareCart() {
      if (this.shareLoading) return;

      try {
        // Показываем индикатор загрузки
        this.shareLoading = true;

        // Получаем текущие товары в корзине
        const cartItems =
          this.cartItems.map((item) => ({
            product_id: item.item.id,
            quantity: item.count,
          })) || [];

        // Проверяем, что корзина не пуста
        if (cartItems.length === 0) {
          this.$notify({
            type: "error",
            title: "Ошибка",
            text: "Корзина пуста",
          });
          return;
        }

        // Выполняем запрос к GraphQL API
        const { data } = await this.$apollo.provider.defaultClient.mutate({
          mutation: CREATE_SHARED_CART,
          variables: {
            input: cartItems,
          },
          context: {
            headers: {
              Authorization: "Bearer " + this.$store.state.apollo_token,
            },
          },
        });

        // Проверяем успешность запроса
        if (data && data.createSharedCart && data.createSharedCart.code === 200) {
          // Получаем hash для создания URL
          const { hash } = data.createSharedCart;

          // Создаем URL с query параметром
          const shareUrl = `${window.location.origin}/cart?hash=${hash}`;

          // Копируем URL в буфер обмена
          this.copyToClipboard(shareUrl);

          // Показываем уведомление об успехе
          this.$notify({
            type: "success",
            title: "Успешно",
            text: "Ссылка на корзину скопирована в буфер обмена",
          });
        } else {
          // Обработка ошибки
          console.error("Ошибка при создании общей корзины:", data?.createSharedCart?.message);
          this.$notify({
            type: "error",
            title: "Ошибка",
            text: "Не удалось создать ссылку на корзину",
          });
        }
      } catch (error) {
        // Обработка ошибки
        console.error("Ошибка при выполнении запроса:", error);
        this.$notify({
          type: "error",
          title: "Ошибка",
          text: "Произошла ошибка при создании ссылки на корзину",
        });
      } finally {
        // Скрываем индикатор загрузки
        this.shareLoading = false;
      }
    },

    /**
     * Копирование текста в буфер обмена
     */
    copyToClipboard(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      document.body.appendChild(textarea);
      textarea.select();

      try {
        document.execCommand("copy");
      } catch (err) {
        console.error("Не удалось скопировать текст:", err);
      }

      document.body.removeChild(textarea);

      this.sharedCartCopied = true;

      setTimeout(() => {
        this.sharedCartCopied = false;
      }, 3000);
    },
  },
  components: {
    SwitchComponent,
    IconComponent,
    InputComponent,
    TermsAgreeComponent,
    FileIconComponent,
  },
};
</script>

<style lang="stylus">
.cart-page-aside {
  display grid
  align-items start
  min-width 300px
  +above(991px) {
    position sticky
    top 24px
  }

  &__main {
    background: var(--white);
    border-radius: var(--main_radius);
    filter: drop-shadow(15px 15px 20px var(--black_o1));
		overflow: hidden
  }

  &__total {
    display flex
    align-items center
    justify-content space-between
    border-bottom 1px solid var(--gray)
    width 100%
    padding 20px
    background var(--gray_o5)

    span {
      font-weight: 700;
      font-size: 1.375em
      line-height: 115%
      display: flex;
      align-items: center;
      color: var(--body-color);
    }
  }

  &__info {
    display grid
    width 100%

    li {
      display flex
      width 100%
      justify-content space-between
      padding 10px 20px

      &.cart-page-aside__discount {
        background var(--green-light)
      }

      span {
        display: flex;
        align-items: center;
        color: var(--black);
      }
    }
  }

  &__pay {
    display: grid;
    grid-gap 10px
    width 100%
    padding 10px 20px
  }

  &__share {
    display: grid;
    grid-gap 10px
    width 100%
    margin-top 20px

    & .btn--main {
      text-transform none
      font-weight normal
    }
  }

  &__promocode {
    display grid
    grid-gap 20px
    padding 10px 20px
    border-top 1px solid var(--gray)

    .input__action {
      transition var(--transition)
      &:hover {
        background var(--gray)
      }

      .icon,
      .file-icon {
        width: 20px
        height: 20px

        svg circle {
          stroke var(--main-color)
        }
      }
    }

    &-active {
      display flex
      align-items center
      justify-content space-between

      span {
        color var(--green)
      }

      .btn {
        padding 5px

        svg path {
          transition var(--transition)
        }

        &:hover {
          background var(--gray)

          svg path {
            stroke var(--red)
          }
        }
      }
    }
  }
}

.cart-page__bonuses {
	display flex
	flex-direction column
	gap: 10px
	background var(--green-light)

	&-balance {
		display flex
		align-items center
		gap: 20px
		justify-content space-between

		&-left {
			display flex
			align-items center
			gap: 8px
			+below(1250px) {
				flex-direction column
				align-items flex-start
				gap: 0
			}
		}

		&-title {
			font-weight: 500;
			font-size: 1.125rem
			line-height: 1.7
			letter-spacing: 0;
		}

		&-subtitle {
			font-size: 0.875rem;
			color: var(--main-color) !important
			line-height: 1
			display flex
			align-items center
			gap: 8px
			padding-top 2px
			+below(1250px) {
				padding-top 0
			}

			&:before {
				content: ""
				display block
				width 4px
				height 4px
				border-radius 50%
				background var(--main-color)
				+below(1250px) {
					display none;
				}
			}
		}

		&-value {
			font-weight: 600;
			line-height: 1
			letter-spacing: 0;
			display flex
			align-items center
			gap: 5px
			padding: 0 14px
			height 27px
			border-radius var(--small_radius)
			background var(--yellow)
			color var(--white) !important

			.icon {
				width 24px
				height 24px

				svg path {
					stroke transparent
				}
			}
		}
	}
}
</style>
